(() => {
  'use strict';
  if (customElements.get('trexo-system-guide')) return;

  class TrexoSystemGuide extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.initialize());
    }

    initialize() {
      if (!this.isConnected) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.nav = this.querySelector('[data-sg-index]');
      this.toc = this.querySelector('[data-sg-toc]');
      this.header = document.querySelector('.site-header[data-sticky]');
      this.links = [...this.nav.querySelectorAll('a[href^="#"]')];
      this.targets = this.links.map((link) => this.querySelector(link.hash));
      this.current = null;
      this.pending = null;
      this.offset = -1;
      this.pins = [...this.querySelectorAll('[data-sg-hotspot]')];
      this.parts = [...this.querySelectorAll('[data-sg-parts] details')];
      this.querySelector('[data-sg-hotspots]')?.removeAttribute('hidden');

      this.pins.forEach((pin) => {
        pin.addEventListener(
          'click',
          () => {
            const target = this.querySelector(`#${pin.getAttribute('aria-controls')}`);
            this.parts.forEach((part) => {
              part.open = part === target;
            });
            this.syncPins();
            const panel = target.getBoundingClientRect();
            if (panel.bottom > innerHeight || panel.top < this.anchorOffset) {
              target.scrollIntoView({ block: 'nearest', behavior: this.scrollBehavior() });
            }
          },
          options,
        );
      });
      this.parts.forEach((part) => part.addEventListener('toggle', () => this.syncPins(), options));

      this.tabs = [...this.querySelectorAll('[data-sg-tabs] [role="tab"]')];
      this.panels = [...this.querySelectorAll('[data-sg-setup-panel]')];
      this.querySelector('[data-sg-tabs]').hidden = false;
      this.panels.forEach((panel, index) => {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', this.tabs[index].id);
        panel.tabIndex = 0;
      });
      this.selectSetup(0);
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.selectSetup(index), options);
        tab.addEventListener(
          'keydown',
          (event) => {
            let next;
            if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
            if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = this.tabs.length - 1;
            if (next === undefined) return;
            event.preventDefault();
            this.selectSetup(next);
            this.tabs[next].focus();
          },
          options,
        );
      });

      this.addEventListener(
        'click',
        (event) => {
          const link = event.target.closest('a[data-sg-open-details]');
          if (!link || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
          const detail = this.querySelector(link.hash);
          if (!detail) return;
          event.preventDefault();
          detail.open = true;
          history.pushState(null, '', link.hash);
          this.update();
          detail.scrollIntoView({ block: 'start', behavior: this.scrollBehavior() });
          detail.querySelector('summary').focus({ preventScroll: true });
        },
        options,
      );

      this.queueUpdate = () => {
        if (this.pending) return;
        this.pending = requestAnimationFrame(() => {
          this.pending = null;
          this.update();
        });
      };
      window.addEventListener('scroll', this.queueUpdate, { passive: true, ...options });
      window.addEventListener('resize', this.queueUpdate, options);
      window.addEventListener('hashchange', () => this.openHash(), options);
      this.observer = new ResizeObserver(this.queueUpdate);
      this.observer.observe(this);
      this.observer.observe(this.toc);
      if (this.header) this.observer.observe(this.header);
      this.update();
      this.openHash();
    }

    scrollBehavior() {
      return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
    }

    openHash() {
      const target = [...this.querySelectorAll('[id]')].find((element) => `#${element.id}` === location.hash);
      if (target?.matches('details')) target.open = true;
      const panel = target?.closest('[data-sg-setup-panel]');
      if (panel) this.selectSetup(this.panels.indexOf(panel));
      if (target) requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
    }

    selectSetup(index) {
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
        this.panels[i].hidden = i !== index;
      });
    }

    syncPins() {
      this.pins.forEach((pin) => {
        const part = this.parts.find((item) => item.id === pin.getAttribute('aria-controls'));
        pin.setAttribute('aria-expanded', String(Boolean(part?.open)));
      });
    }

    update() {
      const offset = Math.max(0, this.header?.getBoundingClientRect().bottom || 0);
      this.anchorOffset = offset + this.toc.getBoundingClientRect().height + 20;
      if (offset !== this.offset) {
        this.style.setProperty('--sg-header-offset', `${offset}px`);
        this.offset = offset;
      }
      this.style.setProperty('--sg-anchor-offset', `${this.anchorOffset}px`);
      this.style.setProperty('--sg-root-padding', getComputedStyle(document.documentElement).scrollPaddingTop);
      let active = 0;
      this.targets.forEach((target, i) => {
        if (target && target.getBoundingClientRect().top <= this.anchorOffset + 24) active = i;
      });
      if (this.current === active) return;
      this.current = active;
      this.links.forEach((link, i) => {
        if (i === active) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
      const link = this.links[active];
      const bounds = this.nav.getBoundingClientRect();
      const item = link.getBoundingClientRect();
      if (item.left < bounds.left || item.right > bounds.right) {
        this.nav.scrollLeft += item.left - bounds.left - (bounds.width - item.width) / 2;
      }
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      if (this.pending) cancelAnimationFrame(this.pending);
      this.events?.abort();
      this.observer?.disconnect();
    }
  }
  customElements.define('trexo-system-guide', TrexoSystemGuide);
})();
